// MARK://dhk8/pos/features/billing/facturar-sheet
// BottomSheet para "facturar desde orden/ticket": captura los datos fiscales del
// receptor (RFC + razón social + Uso CFDI + Régimen fiscal + C.P. + correo),
// permite previsualizar el CFDI 4.0 que se emitiría y luego "Emitir factura".
// La emisión va por la cadena del proveedor fiscal (DocketHub → Omnifiscal →
// FiscalAPI → SAT) — aquí está todo simulado, no se conecta a nada.
//
// Reutiliza features/pos/BottomSheet (portea al <body>). Tokens --pos-*. Sin
// emoji, ≥44pt, responsive (los campos se apilan en <520px).

import { useEffect, useMemo, useState } from 'react'

import BottomSheet from '../pos/BottomSheet.js'
import { formatCents } from '../../lib/money.js'
import { listCustomers, type CustomerDto } from '../../lib/clients/customers.js'
import { issueInvoiceFromOrder, type FiscalReceiverData, type InvoiceDto } from '../../lib/clients/invoices.js'
import { USO_CFDI_OPTIONS, REGIMEN_FISCAL_OPTIONS, FORMA_PAGO_OPTIONS, isValidRfc, labelForUso, labelForRegimen } from './sat-catalogs.js'
import { billingStyles } from './billing-styles.js'

export interface FacturarSheetProps {
  open: boolean
  onClose: () => void
  /** Orden/ticket que se va a facturar. Vacío ⇒ flujo "Facturar" genérico. */
  orderId?: string | null
  /** Total bruto del ticket en centavos (para cuadrar el CFDI). */
  totalCents?: number
  /** Descripción del concepto (ej. "Consumo en restaurante · ticket A-1039"). */
  concept?: string
  /** Folio visible del ticket, sólo para el encabezado del sheet. */
  ticketFolio?: string | null
  /** Se llama con la factura emitida (timbrada, con UUID). */
  onIssued?: (invoice: InvoiceDto) => void
}

type Step = 'form' | 'preview' | 'done'

interface FormState {
  rfc: string
  legalName: string
  usoCfdi: string
  fiscalRegimeCode: string
  zipCode: string
  email: string
  paymentForm: string
}

const EMPTY: FormState = {
  rfc: '',
  legalName: '',
  usoCfdi: 'G03',
  fiscalRegimeCode: '612',
  zipCode: '',
  email: '',
  paymentForm: '01',
}

export default function FacturarSheet({ open, onClose, orderId, totalCents = 0, concept, ticketFolio, onIssued }: FacturarSheetProps) {
  const [step, setStep] = useState<Step>('form')
  const [form, setForm] = useState<FormState>(EMPTY)
  const [customers, setCustomers] = useState<CustomerDto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<InvoiceDto | null>(null)

  // Reset on (re)open.
  useEffect(() => {
    if (!open) return
    setStep('form')
    setForm(EMPTY)
    setError(null)
    setIssued(null)
    const ctrl = new AbortController()
    listCustomers(undefined, ctrl.signal).then(setCustomers).catch(() => setCustomers([]))
    return () => ctrl.abort()
  }, [open])

  const conceptText = concept ?? (ticketFolio ? `Consumo en restaurante · ticket ${ticketFolio}` : 'Consumo en restaurante')

  function prefillFromCustomer(id: string) {
    const c = customers.find((x) => x.id === id)
    if (!c) return
    setForm({
      rfc: c.rfc,
      legalName: c.legalName,
      usoCfdi: c.defaultCfdiUsage ?? 'G03',
      fiscalRegimeCode: c.fiscalRegimeCode ?? '612',
      zipCode: c.zipCode ?? '',
      email: c.email ?? '',
      paymentForm: c.defaultPaymentForm ?? '01',
    })
  }

  const formValid = useMemo(() => {
    return (
      isValidRfc(form.rfc) &&
      form.legalName.trim().length > 1 &&
      /^\d{5}$/.test(form.zipCode.trim()) &&
      (form.email.trim() === '' || /.+@.+\..+/.test(form.email.trim()))
    )
  }, [form])

  function goPreview() {
    if (!formValid) {
      setError('Revisa el RFC (12–13 caracteres), la razón social y el C.P. (5 dígitos).')
      return
    }
    setError(null)
    setStep('preview')
  }

  async function emit() {
    setBusy(true)
    setError(null)
    try {
      const fiscalData: FiscalReceiverData = {
        rfc: form.rfc.trim().toUpperCase(),
        legalName: form.legalName.trim(),
        usoCfdi: form.usoCfdi,
        fiscalRegimeCode: form.fiscalRegimeCode,
        zipCode: form.zipCode.trim(),
        email: form.email.trim() || undefined,
        paymentForm: form.paymentForm,
        totalCents: totalCents > 0 ? totalCents : undefined,
        concept: conceptText,
      }
      const inv = await issueInvoiceFromOrder(orderId ?? `ord_${Math.random().toString(36).slice(2, 8)}`, fiscalData)
      setIssued(inv)
      setStep('done')
      onIssued?.(inv)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo emitir la factura')
    } finally {
      setBusy(false)
    }
  }

  const previewBase = totalCents > 0 ? Math.max(1, Math.round(totalCents / 1.16)) : 0
  const previewTax = totalCents > 0 ? totalCents - previewBase : 0

  const title = step === 'done' ? 'Factura emitida' : ticketFolio ? `Facturar ticket ${ticketFolio}` : 'Facturar'

  return (
    <BottomSheet open={open} onClose={onClose} title={title} ariaLabel="Facturar ticket" heightFraction={0.9}>
      <div className="dhk8-bil dhk8-bil--sheet">
        <p className="dhk8-bil__sub" style={{ padding: '0 16px' }}>
          La emisión va por la cadena del proveedor fiscal: DocketHub → Omnifiscal → FiscalAPI → SAT. (Entorno de demostración — no se conecta a ningún servicio real.)
        </p>

        {error && <div className="dhk8-bil__error" style={{ margin: '0 16px' }}>{error}</div>}

        {step === 'form' && (
          <div className="dhk8-bil__card" style={{ margin: '0 16px 16px', boxShadow: 'none' }}>
            {customers.length > 0 && (
              <label>
                Cliente guardado (opcional)
                <select defaultValue="" onChange={(e) => { if (e.target.value) prefillFromCustomer(e.target.value) }}>
                  <option value="">— Capturar manualmente —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.legalName} ({c.rfc})</option>)}
                </select>
              </label>
            )}
            <div className="dhk8-bil__form">
              <label>
                RFC receptor *
                <input value={form.rfc} maxLength={13} placeholder="XAXX010101000"
                  onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))} />
              </label>
              <label>
                Razón social *
                <input value={form.legalName} placeholder="Nombre o denominación"
                  onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))} />
              </label>
              <label>
                Uso CFDI *
                <select value={form.usoCfdi} onChange={(e) => setForm((f) => ({ ...f, usoCfdi: e.target.value }))}>
                  {USO_CFDI_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </label>
              <label>
                Régimen fiscal *
                <select value={form.fiscalRegimeCode} onChange={(e) => setForm((f) => ({ ...f, fiscalRegimeCode: e.target.value }))}>
                  {REGIMEN_FISCAL_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </label>
              <label>
                Código postal *
                <input value={form.zipCode} maxLength={5} inputMode="numeric" placeholder="03100"
                  onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value.replace(/\D/g, '') }))} />
              </label>
              <label>
                Forma de pago
                <select value={form.paymentForm} onChange={(e) => setForm((f) => ({ ...f, paymentForm: e.target.value }))}>
                  {FORMA_PAGO_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </label>
              <label>
                Email de envío
                <input type="email" value={form.email} placeholder="cliente@correo.com"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </label>
            </div>
            <div className="dhk8-bil__actions">
              <button type="button" className="dhk8-bil__btn dhk8-bil__btn--primary" onClick={goPreview} disabled={!formValid}>Previsualizar</button>
              <button type="button" className="dhk8-bil__btn" onClick={onClose}>Cancelar</button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="dhk8-bil__card" style={{ margin: '0 16px 16px' }}>
            <h2>Vista previa del CFDI 4.0</h2>
            <table className="dhk8-bil__kv">
              <tbody>
                <tr><td>Emisor</td><td>DocketHub POS · RFC emisor en certificado</td></tr>
                <tr><td>Receptor</td><td>{form.legalName}</td></tr>
                <tr><td>RFC receptor</td><td><code>{form.rfc.toUpperCase()}</code></td></tr>
                <tr><td>Régimen receptor</td><td>{labelForRegimen(form.fiscalRegimeCode)}</td></tr>
                <tr><td>C.P. receptor</td><td>{form.zipCode}</td></tr>
                <tr><td>Uso CFDI</td><td>{labelForUso(form.usoCfdi)}</td></tr>
                <tr><td>Forma de pago</td><td>{FORMA_PAGO_OPTIONS.find((o) => o.code === form.paymentForm)?.label ?? form.paymentForm}</td></tr>
                <tr><td>Método de pago</td><td>PUE — Pago en una sola exhibición</td></tr>
                {orderId && <tr><td>Orden</td><td><code>{orderId}</code></td></tr>}
                {form.email && <tr><td>Enviar a</td><td>{form.email}</td></tr>}
              </tbody>
            </table>
            <h2>Conceptos</h2>
            <table className="dhk8-bil__kv">
              <tbody>
                <tr><td>{conceptText}</td><td className="dhk8-bil__money">{formatCents(totalCents > 0 ? previewBase : 0)}</td></tr>
              </tbody>
            </table>
            <table className="dhk8-bil__kv">
              <tbody>
                <tr><td>Subtotal</td><td className="dhk8-bil__money">{formatCents(previewBase)}</td></tr>
                <tr><td>IVA 16%</td><td className="dhk8-bil__money">{formatCents(previewTax)}</td></tr>
                <tr><td><strong>Total</strong></td><td className="dhk8-bil__money"><strong>{formatCents(totalCents)}</strong></td></tr>
              </tbody>
            </table>
            <p className="dhk8-bil__notice" style={{ margin: 0 }}>Al emitir, se solicita el timbrado al proveedor fiscal y se obtiene el folio fiscal (UUID).</p>
            <div className="dhk8-bil__actions">
              <button type="button" className="dhk8-bil__btn dhk8-bil__btn--primary" onClick={emit} disabled={busy}>{busy ? 'Emitiendo…' : 'Emitir factura'}</button>
              <button type="button" className="dhk8-bil__btn" onClick={() => setStep('form')} disabled={busy}>Volver a editar</button>
            </div>
          </div>
        )}

        {step === 'done' && issued && (
          <div className="dhk8-bil__card" style={{ margin: '0 16px 16px' }}>
            <h2>Factura {issued.serie}-{issued.folio} timbrada</h2>
            <table className="dhk8-bil__kv">
              <tbody>
                <tr><td>Folio fiscal (UUID)</td><td><code style={{ fontSize: 'var(--pos-text-xxs,11px)' }}>{issued.cfdiUuid}</code></td></tr>
                <tr><td>Receptor</td><td>{issued.customer?.legalName ?? form.legalName} ({issued.customer?.rfc ?? form.rfc.toUpperCase()})</td></tr>
                <tr><td>Total</td><td className="dhk8-bil__money">{formatCents(issued.totalCents)}</td></tr>
                <tr><td>Estado</td><td>Timbrada</td></tr>
              </tbody>
            </table>
            {form.email && <p className="dhk8-bil__notice" style={{ margin: 0 }}>Se envió una copia (XML + PDF) a {form.email}.</p>}
            <div className="dhk8-bil__actions">
              <button type="button" className="dhk8-bil__btn dhk8-bil__btn--primary" onClick={onClose}>Listo</button>
            </div>
          </div>
        )}
      </div>
      <style>{billingStyles}</style>
    </BottomSheet>
  )
}
